/**
 * InstrumentSelector Component - Enhanced instrument selection
 * Custom dropdown with tooltips for each instrument
 */

import { memo, useMemo, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { InstrumentType } from '../../types/composition';
import type { InstrumentParameters } from '../../types/instruments';
import { instrumentDatabase } from '../../services/instruments/InstrumentDatabase';
import { InstrumentIcon } from './InstrumentIcon';
import { InstrumentErrorBoundary } from '../ErrorBoundary';
import './InstrumentSelector.css';

/**
 * Props for InstrumentSelector component
 */
interface InstrumentSelectorProps {
  /** Current instrument value */
  value: InstrumentType;
  /** Callback when instrument changes */
  onChange: (instrument: InstrumentType, parameters?: InstrumentParameters) => void;
  /** Row index for accessibility */
  row: number;
}

/**
 * InstrumentSelector component (internal)
 * Custom dropdown that shows tooltips when hovering over instruments
 */
const InstrumentSelectorComponent = memo(function InstrumentSelector({
  value,
  onChange,
  row,
}: InstrumentSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredInstrument, setHoveredInstrument] = useState<InstrumentType | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hoveredItemRef = useRef<HTMLDivElement>(null);
  
  // Memoize categories to avoid recalculation
  const categories = useMemo(() => instrumentDatabase.getCategories(), []);
  
  // Memoize current instrument lookup
  const currentInstrument = useMemo(
    () => instrumentDatabase.getInstrument(value),
    [value]
  );

  // Memoize hovered instrument lookup
  const hoveredInstrumentData = useMemo(
    () => hoveredInstrument ? instrumentDatabase.getInstrument(hoveredInstrument) : null,
    [hoveredInstrument]
  );

  // Memoize category instruments to avoid recalculation on every render
  const categoryInstruments = useMemo(() => {
    return categories.map((category) => ({
      category,
      instruments: instrumentDatabase.getInstrumentsByCategory(category),
    }));
  }, [categories]);

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [isOpen]);

  // Update tooltip position when hovering over an instrument
  useEffect(() => {
    if (hoveredInstrument && hoveredItemRef.current && dropdownRef.current) {
      const itemRect = hoveredItemRef.current.getBoundingClientRect();
      const dropdownRect = dropdownRef.current.getBoundingClientRect();
      
      setTooltipPosition({
        top: itemRect.top,
        left: dropdownRect.right + 8, // 8px gap from dropdown
      });
    }
  }, [hoveredInstrument]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  /**
   * Handle instrument selection
   */
  const handleSelect = (instrumentId: InstrumentType) => {
    const instrument = instrumentDatabase.getInstrument(instrumentId);
    if (instrument) {
      onChange(instrument.id, instrument.defaultParameters);
      setIsOpen(false);
      setHoveredInstrument(null);
    }
  };

  return (
    <div className="instrument-selector-wrapper">
      {/* Trigger button */}
      <button
        ref={triggerRef}
        type="button"
        className="instrument-selector-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={`Instrument for row ${row + 1}: ${currentInstrument?.displayName || 'Select instrument'}`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        {currentInstrument && (
          <InstrumentIcon
            icon={currentInstrument.icon}
            category={currentInstrument.category}
            size="md"
            className="instrument-selector-icon"
            aria-hidden="true"
          />
        )}
        <span className="instrument-selector-name">
          {currentInstrument?.displayName || 'Select instrument'}
        </span>
        <span className="instrument-selector-arrow" aria-hidden="true">▼</span>
      </button>

      {/* Custom dropdown using portal */}
      {isOpen && createPortal(
        <div 
          ref={dropdownRef}
          className="instrument-dropdown" 
          role="listbox"
          style={{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
          }}
        >
          {categoryInstruments.map(({ category, instruments }) => (
            <div key={category} className="instrument-category-group">
              <div className="instrument-category-label">{category}</div>
              {instruments.map((instrument) => (
                <div
                  key={instrument.id}
                  ref={hoveredInstrument === instrument.id ? hoveredItemRef : null}
                  className={`instrument-option ${instrument.id === value ? 'selected' : ''}`}
                  role="option"
                  aria-selected={instrument.id === value}
                  onClick={() => handleSelect(instrument.id)}
                  onMouseEnter={() => setHoveredInstrument(instrument.id)}
                  onMouseLeave={() => setHoveredInstrument(null)}
                >
                  <InstrumentIcon
                    icon={instrument.icon}
                    category={instrument.category}
                    size="sm"
                    className="instrument-option-icon"
                    aria-hidden="true"
                  />
                  <span className="instrument-option-name">{instrument.displayName}</span>
                  {instrument.id === value && (
                    <span className="instrument-option-check" aria-hidden="true">✓</span>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>,
        document.body
      )}

      {/* Tooltip for hovered instrument - using portal for proper positioning */}
      {hoveredInstrumentData && isOpen && createPortal(
        <div 
          className="instrument-tooltip" 
          role="tooltip"
          style={{
            position: 'fixed',
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
          }}
        >
          <div className="instrument-tooltip-header">
            <span className="instrument-tooltip-icon">{hoveredInstrumentData.icon}</span>
            <span className="instrument-tooltip-name">{hoveredInstrumentData.displayName}</span>
            <span className="instrument-tooltip-category">{hoveredInstrumentData.category}</span>
          </div>
          <div className="instrument-tooltip-description">
            {hoveredInstrumentData.description}
          </div>
          {hoveredInstrumentData.tags && hoveredInstrumentData.tags.length > 0 && (
            <div className="instrument-tooltip-tags">
              {hoveredInstrumentData.tags.map((tag, index) => (
                <span key={index} className="instrument-tooltip-tag">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>,
        document.body
      )}
      
      <span id={`instrument-desc-${row}`} className="sr-only">
        {currentInstrument?.description || 'Select an instrument for this row'}
      </span>
    </div>
  );
});

/**
 * Export InstrumentSelector wrapped in error boundary
 */
export const InstrumentSelector = function InstrumentSelectorWithBoundary(props: InstrumentSelectorProps) {
  return (
    <InstrumentErrorBoundary>
      <InstrumentSelectorComponent {...props} />
    </InstrumentErrorBoundary>
  );
};
